import type { SelectHTMLAttributes } from 'react';
import type { Option } from '@fkwd-qc/shared';

/**
 * Props for a picker. Everything a native select accepts passes through, so a caller can
 * still hand it the id and aria attributes its own field wrapper produced.
 *
 * @since 1.4.0
 */
export interface OptionSelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children' | 'onChange'> {
    options: Option[];
    onValueChange: (value: string) => void;
    /** The label of the empty first choice; omit it when the picker always has a value. */
    placeholder?: string;
}

/**
 * Renders one choice, naming its hint alongside the label when it has one so two similar
 * labels can be told apart.
 *
 * @since 1.4.0
 * @param option - the choice to word
 * @returns the text to show
 */
function optionLabel(option: Option): string {
    return option.hint === null || option.hint === '' ? option.label : `${option.label} (${option.hint})`;
}

/**
 * The select every picker is drawn with, so choices read and behave the same wherever one
 * appears. It renders choices and nothing else: the label, the field wrapper and where the
 * choices come from all stay with the caller.
 *
 * @since 1.4.0
 * @param props - the choices, the current value, the change handler, and any select attributes
 * @returns the select element
 */
export function OptionSelect({ options, onValueChange, placeholder, ...selectProps }: OptionSelectProps) {
    return (
        <select {...selectProps} onChange={(event) => onValueChange(event.target.value)}>
            {placeholder === undefined ? null : <option value="">{placeholder}</option>}
            {options.map((option) => (
                <option key={option.value} value={option.value}>
                    {optionLabel(option)}
                </option>
            ))}
        </select>
    );
}
